import * as L from 'leaflet';
import { FeatureCollection, Geometry } from 'geojson';
import { take } from 'rxjs/operators';
import { feedsConfig } from 'src/app/services/constants';
import { FilterPopupComponent } from '../collections/filter-popup/filter-popup.component';
import {
  buildOilSpillLegendGroupsFromResults,
  getOilSpillDetectionClasses,
  getOilSpillForecastHours,
} from './oil-spill-legend/oil-spill-legend.model';

type FeatureProps = any;

export type OilSpillPredictionFilterOption = {
  key: string;
  label: string;
  hours: Array<string | number>;
};

export type OilSpillPredictionFilterSelection = {
  detectionClass?: string | number;
  forecastHour?: string | number | null;
};

export class OilSpillPredictionController {
  constructor(private readonly host: any) {}

  openScopedFilterDialog(args: {
    sensor: any;
    sensorIndex: number;
    filterType: any;
    state: any;
    isCompare?: boolean;
  }): void {
    const { sensor, sensorIndex, filterType, state, isCompare } = args;
    const { type, label, feedKeys, popupLabel, placeholderLabel } = filterType;
    const dialogRef = this.openDialog({
      feedKeys,
      filterLabel: popupLabel || label,
      placeholderLabel,
      currentFilterState: state.filtersByType.get(type),
      isCompare,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: any) => {
        if (!result) return;

        if (result.clear) {
          this.host.clearFilterForSensor(sensor, sensorIndex, type);
          return;
        }

        if (!result.value?.detectionClass) return;

        const activeState = this.host.activateSensorFilterScope(sensor, sensorIndex);
        activeState.selectedFilterType = type;
        activeState.filtersByType.set(type, {
          feedKeys,
          value: result.value,
        });

        this.applyFilter(feedKeys, result.value);
      });
  }

  openGlobalFilterDialog(args: {
    filterType: any;
    currentFilterState: any;
    isCompare?: boolean;
  }): void {
    const { filterType, currentFilterState, isCompare } = args;
    const { type, label, feedKeys, popupLabel, placeholderLabel } = filterType;
    const dialogRef = this.openDialog({
      feedKeys,
      filterLabel: popupLabel || label,
      placeholderLabel,
      currentFilterState,
      isCompare,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((result: any) => {
        if (!result) return;

        if (result?.clear) {
          this.host.filtersByType.delete(type);
          this.host.clearFilterByType(type);
          return;
        }

        if (!result?.value?.detectionClass) return;

        this.host.filtersByType.set(type, {
          feedKeys,
          value: result.value,
        });

        this.applyFilter(feedKeys, result.value);
      });
  }

  private openDialog(args: {
    feedKeys: string[];
    filterLabel: string;
    placeholderLabel?: string;
    currentFilterState: any;
    isCompare?: boolean;
  }): any {
    return this.host.matdialog.open(FilterPopupComponent, {
      width: '35%',
      backdropClass: 'blurr',
      disableClose: true,
      data: {
        type: 'oilSpillPredictionFilter',
        oilSpillOptions: this.getFilterOptions(args.feedKeys),
        filterLabel: args.filterLabel,
        placeholderLabel: args.placeholderLabel || 'Select detection',
        currentFilterState: args.currentFilterState,
        isCompare: args.isCompare ?? false,
      },
    });
  }

  getFilterOptions(feedKeys: string[]): OilSpillPredictionFilterOption[] {
    const detections = new Map<string, Set<string>>();

    for (const feedKey of feedKeys || []) {
      const entry = this.host.geoByFeed.get(String(feedKey));
      const feedResults = entry?.feed_results ?? entry?.fc?.feed_results;
      const hours = getOilSpillForecastHours(feedResults).map(String);
      for (const detectionClass of getOilSpillDetectionClasses(feedResults)) {
        if (!detections.has(detectionClass)) {
          detections.set(detectionClass, new Set<string>());
        }
        hours.forEach((hour) => detections.get(detectionClass)!.add(hour));
      }
    }

    return Array.from(detections.entries())
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([detectionClass, hours]) => ({
        key: detectionClass,
        label: `Detection ${detectionClass}`,
        hours: Array.from(hours).sort((a, b) => Number(a) - Number(b)),
      }));
  }

  applyFilter(
    feedKeys: string[],
    selection: OilSpillPredictionFilterSelection
  ): void {
    if (!selection?.detectionClass) return;

    this.host.unregisterOilSpillTimeseries(false);
    const managedFeeds: Set<string> = this.host.refreshViewportFeedFilters();
    this.host.clearFilteredLayerAndRestore(true, undefined, true);
    this.host.removeISLayers(true);
    this.host.feedPanels = new Map(
      [...this.host.feedPanels].filter(([key]) => managedFeeds.has(key))
    );

    const combinedFeatures: GeoJSON.Feature[] = [];
    const activeEntries: Array<[string, any]> = [];
    const oilSpillFeedKeySet = new Set((feedKeys || []).map((key) => String(key)));

    for (const key of this.host.activeFeedKeys) {
      const entry = this.host.geoByFeed.get(key);
      if (entry) activeEntries.push([key, entry]);
    }

    for (const [feedKey, entry] of activeEntries) {
      if (managedFeeds.has(feedKey)) continue;
      const cfg = feedsConfig[feedKey];
      if (!cfg || !entry.fc) continue;

      const sourceFeatures = Array.isArray(entry.fc.features) ? entry.fc.features : [];
      const isFilteredOilSpillFeed =
        oilSpillFeedKeySet.has(feedKey) && this.host.isOilSpillPredictionConfig(cfg);
      const renderedFeatures = isFilteredOilSpillFeed
        ? this.filterFeatures(sourceFeatures, selection)
        : sourceFeatures;

      const renderedCollection: FeatureCollection<Geometry, FeatureProps> = {
        ...entry.fc,
        type: 'FeatureCollection',
        features: renderedFeatures as any,
      };
      const color = entry.color || cfg?.color || '#ffd166';
      const feedResults = this.host.isOilSpillPredictionConfig(cfg)
        ? this.buildFeedResultsForSections((entry.fc as any)?.feed_results)
        : (entry.fc as any)?.feed_results;
      const vm = this.host.helperService.buildFeedSections(
        cfg,
        isFilteredOilSpillFeed
          ? {
              ...feedResults,
              forecast_classes: [selection.detectionClass],
              selected_detection: `Detection ${selection.detectionClass}`,
              selected_forecast_hour: selection.forecastHour
                ? `${selection.forecastHour}h`
                : 'All hours',
              ...(selection.forecastHour != null && Number.isFinite(Number(selection.forecastHour))
                ? { forecast_hours: [Number(selection.forecastHour)] }
                : {}),
            }
          : feedResults,
        color
      );
      const oilSpillLegendGroups = this.host.isOilSpillPredictionConfig(cfg)
        ? buildOilSpillLegendGroupsFromResults(
            (entry.fc as any)?.feed_results,
            cfg,
            String(color ?? '#FF00FF'),
            feedKey,
            isFilteredOilSpillFeed ? selection : undefined
          )
        : undefined;

      this.host.addFeedPanel(feedKey, entry.title, color, vm.sections, oilSpillLegendGroups);

      if (renderedFeatures.length === 0) continue;

      combinedFeatures.push(...(renderedFeatures as any));

      const paneName = this.host.isOilSpillPredictionConfig(cfg)
        ? `oil-spill-prediction-pane-${feedKey}`
        : 'nonVesselPane';
      const pane = this.host.map.getPane(paneName) || this.host.map.createPane(paneName);
      pane.style.zIndex = this.host.isOilSpillPredictionConfig(cfg)
        ? this.host.oilSpillPredictionPaneZIndex
        : '7000';
      pane.style.pointerEvents = 'auto';

      const filteredLayer = this.host.buildFeedGeoJsonLayer(
        renderedCollection,
        cfg,
        color,
        cfg?.name || entry.title || feedKey,
        paneName
      );

      this.host.currentFilteredLayers[feedKey] = filteredLayer;
      filteredLayer.addTo(this.host.map);

      if (this.host.isOilSpillPredictionConfig(cfg)) {
        this.host.liftOilSpillPredictionLayer(filteredLayer);
        const forecastDetails = this.getForecastDetailsForFeatures(
          renderedFeatures,
          renderedCollection,
          entry.fc,
          entry
        );
        if (forecastDetails.length) {
          (renderedCollection as any).forecast_details = forecastDetails;
          this.host.registerOilSpillTimeseries(
            feedKey,
            entry,
            renderedCollection,
            filteredLayer
          );
        }
      }
    }

    if (combinedFeatures.length > 0 && managedFeeds.size === 0) {
      const allLayer = (L as any).geoJSON({
        type: 'FeatureCollection',
        features: combinedFeatures,
      });
      this.host.map.fitBounds(allLayer.getBounds());
    } else if (combinedFeatures.length === 0 && managedFeeds.size === 0) {
      this.host.showNoMatchesMessage();
    }

    this.host.rebuildCombined();
    this.host.feedLoaded = true;
  }

  buildFeedResultsForSections(feedResults: any): any {
    // Viewport WFS pages are only for the map; legend metadata comes from the information service.
    return feedResults ?? {};
  }

  getFeatureForecastHour(feature: any): string {
    const forecastHour =
      feature?.properties?.forecast_h ?? feature?.properties?.forecast_hour;
    return forecastHour == null ? '' : String(forecastHour).trim();
  }

  filterFeatures(
    features: any[] = [],
    selection: OilSpillPredictionFilterSelection
  ): any[] {
    const selectedDetection = String(selection?.detectionClass ?? '').trim();
    const selectedHour = String(selection?.forecastHour ?? '').trim();

    if (!selectedDetection) return features;

    return (features ?? []).filter((feature: any) => {
      const detectionClass = this.getFeatureDetectionClass(feature);
      if (detectionClass !== selectedDetection) return false;
      if (this.isDetectionFeature(feature)) return true;
      if (!selectedHour) return true;
      return this.getFeatureForecastHour(feature) === selectedHour;
    });
  }

  private getForecastDetailsForFeatures(
    features: any[] = [],
    ...sources: any[]
  ): any[] {
    const visibleHours = new Set(
      (features ?? [])
        .filter((feature: any) => this.isForecastFeature(feature))
        .map((feature: any) => this.getFeatureForecastHour(feature))
        .filter((hour: string) => !!hour)
    );

    return this.host.getOilSpillForecastDetails(...sources).filter((detail: any) =>
      visibleHours.has(String(detail.forecast_h))
    );
  }

  private getFeatureDetectionClass(feature: any): string {
    const detectionClass = feature?.properties?.class;
    return detectionClass == null ? '' : String(detectionClass).trim();
  }

  private isDetectionFeature(feature: any): boolean {
    const geometryType = feature?.geometry?.type;
    return geometryType === 'Polygon' || geometryType === 'MultiPolygon';
  }

  private isForecastFeature(feature: any): boolean {
    const geometryType = feature?.geometry?.type;
    return geometryType === 'Point' || geometryType === 'MultiPoint';
  }
}
